import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import {
  getSession,
  ApiError,
  login as apiLogin,
  logout as apiLogout,
  registerCompany as apiRegisterCompany,
  verifyTwoFactor,
  type RegisterCompanyInput,
  type Session
} from "./api";
import{clearOfflineData,initialiseOffline}from"./offline";

type AuthContextValue = {
  loading: boolean;
  session: Session | null;
  login(email: string, password: string): Promise<{challengeToken?:string}>;
  completeTwoFactor(challengeToken:string,code:string,rememberDevice:boolean):Promise<void>;
  registerCompany(input: RegisterCompanyInput): Promise<{verificationRequired:boolean;emailSent:boolean}>;
  logout(): Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState<Session | null>(null);

  useEffect(() => {
    void getSession()
      .then(value=>{setSession(value);if(value)void initialiseOffline(value.userId);})
      .catch(() => setSession(null))
      .finally(() => setLoading(false));
  }, []);

  const value = useMemo<AuthContextValue>(() => ({
    loading,
    session,
    async login(email, password) {
      const result=await apiLogin(email, password);
      if(result.twoFactorRequired)return{challengeToken:result.challengeToken};
      const authenticated = await getSession();
      if (!authenticated) throw new ApiError("Your browser blocked the LoadSub session cookie. Allow cookies for this site and try again.", 401, "SESSION_COOKIE_BLOCKED");
      setSession(authenticated);
      await initialiseOffline(authenticated.userId);
      return{};
    },
    async completeTwoFactor(challengeToken,code,rememberDevice){await verifyTwoFactor(challengeToken,code,rememberDevice);const authenticated=await getSession();if(!authenticated)throw new ApiError("Your browser blocked the LoadSub session cookie.",401,"SESSION_COOKIE_BLOCKED");setSession(authenticated);await initialiseOffline(authenticated.userId);},
    async registerCompany(input) {
      const result=await apiRegisterCompany(input);
      if(result.verificationRequired)return result;
      const authenticated = await getSession();
      if (!authenticated) throw new ApiError("Your browser blocked the LoadSub session cookie. Allow cookies for this site and try again.", 401, "SESSION_COOKIE_BLOCKED");
      setSession(authenticated);
      await initialiseOffline(authenticated.userId);
      return result;
    },
    async logout() {
      await apiLogout();
      await clearOfflineData();
      clearOnboardingDismissals();
      setSession(null);
    }
  }), [loading, session]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

function clearOnboardingDismissals(){
  try{
    for(let index=window.sessionStorage.length-1;index>=0;index--){
      const key=window.sessionStorage.key(index);
      if(key?.startsWith("loadsub:onboarding-goal-dismissed:"))window.sessionStorage.removeItem(key);
    }
  }catch{/* Session storage may be unavailable in privacy-restricted browsers. */}
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be used within AuthProvider");
  return value;
}
